import { useState } from 'react';
import { mockAuditLog } from '@/mocks/audit';
import BaseSelect from '@/components/base/BaseSelect';
import { Search } from 'lucide-react';
import { format } from 'date-fns';

export default function AuditLogPage() {
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');

  const filtered = mockAuditLog.filter(entry => {
    if (search) {
      const q = search.toLowerCase();
      if (!entry.action.toLowerCase().includes(q) &&
          !entry.entity_table.toLowerCase().includes(q) &&
          !(entry.user_email || '').toLowerCase().includes(q)) {
        return false;
      }
    }
    if (entityFilter !== 'all' && entry.entity_table !== entityFilter) {
      return false;
    }
    return true;
  }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const entities = Array.from(new Set(mockAuditLog.map(a => a.entity_table)));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Audit Log</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{filtered.length} entries</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md group">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            placeholder="Search actions, entities, users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl outline-none transition-all"
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
          />
        </div>
        <BaseSelect
          value={entityFilter}
          onChange={val => setEntityFilter(val)}
          options={[{ value: 'all', label: 'All entities' }, ...entities.map(e => ({ value: e, label: e }))]}
        />
      </div>

      {/* Card Feed — No Table */}
      <div className="rounded-xl border overflow-hidden"
        style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
      >
        {filtered.map(entry => (
          <div key={entry.id} className="px-5 py-4 transition-colors hover-surface"
            style={{ borderBottom: '1px solid var(--border-separator)' }}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{entry.action}</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded border"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                  >{entry.entity_table}</span>
                </div>
                <p className="text-xs mt-1.5" style={{ color: 'var(--text-tertiary)' }}>
                  by {entry.user_email || 'system'} · {entry.user_role}
                </p>
                {entry.before_data && entry.after_data && (
                  <pre className="text-[10px] font-mono rounded-lg px-2.5 py-1.5 border mt-1.5 overflow-auto"
                    style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', borderColor: 'var(--border-separator)' }}
                  >
                    {JSON.stringify(entry.before_data)} → {JSON.stringify(entry.after_data)}
                  </pre>
                )}
              </div>
              <span className="text-[10px] whitespace-nowrap ml-4" style={{ color: 'var(--text-muted)' }}>
                {format(new Date(entry.created_at), 'MMM d, HH:mm')}
              </span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-8">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No audit entries found</p>
          </div>
        )}
      </div>
    </div>
  );
}